/**
 * Utilitário para manipulação do localStorage com tipagem forte
 * Chaves são constantes definidas na classe
 * Lança erros quando valores não estão disponíveis
 */

import { OauthResult } from '../interfaces/oauth.interface';

export class LocalStorageUtil {
  // ========== CONSTANTES DAS CHAVES ==========
  private static readonly KEY_VW_USER = 'vw_user';
  private static readonly KEY_ACCESS_TOKEN = 'access_vw_token';
  private static readonly KEY_SELECTED_VW_DN = 'selectedVWDn';
  private static readonly KEY_SSO_AUTHENTICATED = 'vw_sso_authenticated';
  private static readonly KEY_LOGIN_USERNAME = 'vw_login_username';

  // ========== MÉTODOS PARA VW_USER ==========
  
  /**
   * Obtém os dados do usuário VW do localStorage
   * @returns Objeto do usuário VW
   * @throws Error se a chave não existir ou estiver vazia
   */
  static getVWUser(): OauthResult {
    const value = localStorage.getItem(this.KEY_VW_USER);
    
    if (value === null || value === undefined || value.trim() === '') {
      throw new Error(`LocalStorage key "${this.KEY_VW_USER}" não encontrada ou está vazia`);
    }
    
    try {
      return JSON.parse(value) as OauthResult;
    } catch (error) {
      throw new Error(`LocalStorage key "${this.KEY_VW_USER}" contém um JSON inválido: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Define os dados do usuário VW no localStorage
   * @param user - Objeto do usuário VW
   */
  static setVWUser(user: OauthResult | object): void {
    localStorage.setItem(this.KEY_VW_USER, JSON.stringify(user));
  }

  /**
   * Obtém os dados do usuário VW ou retorna um valor padrão
   * @param defaultValue - Valor padrão caso a chave não exista
   * @returns Objeto do usuário VW ou valor padrão
   */
  static getVWUserOrDefault(defaultValue: OauthResult): OauthResult {
    try {
      return this.getVWUser();
    } catch {
      return defaultValue;
    }
  }

  /**
   * Verifica se o usuário VW está salvo no localStorage
   * @returns true se a chave existe e não está vazia
   */
  static hasVWUser(): boolean {
    const value = localStorage.getItem(this.KEY_VW_USER);
    return value !== null && value !== undefined && value.trim() !== '';
  }

  /**
   * Remove os dados do usuário VW do localStorage
   */
  static removeVWUser(): void {
    localStorage.removeItem(this.KEY_VW_USER);
  }

  // ========== MÉTODOS PARA ACCESS_TOKEN ==========

  /**
   * Obtém o token de acesso do localStorage
   * @returns Token de acesso como string
   * @throws Error se a chave não existir ou estiver vazia
   */
  static getAccessToken(): string {
    const value = localStorage.getItem(this.KEY_ACCESS_TOKEN);
    
    if (value === null || value === undefined || value.trim() === '') {
      throw new Error(`LocalStorage key "${this.KEY_ACCESS_TOKEN}" não encontrada ou está vazia`);
    }
    
    return value;
  }

  /**
   * Define o token de acesso no localStorage
   * @param token - Token de acesso
   */
  static setAccessToken(token: string): void {
    localStorage.setItem(this.KEY_ACCESS_TOKEN, token);
    //console.log('Token atualizado:', token);
  }

  /**
   * Obtém o token de acesso ou retorna um valor padrão
   * @param defaultValue - Valor padrão caso a chave não exista
   * @returns Token de acesso ou valor padrão
   */
  static getAccessTokenOrDefault(defaultValue: string): string {
    try {
      return this.getAccessToken();
    } catch {
      return defaultValue;
    }
  }

  /**
   * Verifica se o token de acesso está salvo no localStorage
   * @returns true se a chave existe e não está vazia
   */
  static hasAccessToken(): boolean {
    const value = localStorage.getItem(this.KEY_ACCESS_TOKEN);
    return value !== null && value !== undefined && value.trim() !== '';
  }

  /**
   * Remove o token de acesso do localStorage
   */
  static removeAccessToken(): void {
    localStorage.removeItem(this.KEY_ACCESS_TOKEN);
  }

  // ========== MÉTODOS PARA LOGIN_USERNAME ==========

  static getLoginUsername(): string {
    const value = localStorage.getItem(this.KEY_LOGIN_USERNAME);

    if (value === null || value === undefined || value.trim() === '') {
      throw new Error(`LocalStorage key "${this.KEY_LOGIN_USERNAME}" não encontrada ou está vazia`);
    }

    return value;
  }

  static getLoginUsernameOrDefault(defaultValue: string): string {
    try {
      return this.getLoginUsername();
    } catch {
      return defaultValue;
    }
  }

  static setLoginUsername(username: string): void {
    localStorage.setItem(this.KEY_LOGIN_USERNAME, username);
  }

  static removeLoginUsername(): void {
    localStorage.removeItem(this.KEY_LOGIN_USERNAME);
  }

  // ========== MÉTODOS PARA SELECTED_VW_DN ==========

  /**
   * Obtém o DN selecionado do localStorage como string
   * @returns DN selecionado como string
   * @throws Error se a chave não existir ou estiver vazia
   */
  static getSelectedVWDn(): string {
    const value = localStorage.getItem(this.KEY_SELECTED_VW_DN);
    
    if (value === null || value === undefined || value.trim() === '') {
      throw new Error(`LocalStorage key "${this.KEY_SELECTED_VW_DN}" não encontrada ou está vazia`);
    }
    
    return value;
  }

  /**
   * Obtém o DN selecionado do localStorage como number
   * @param radix - Base numérica (padrão: 10)
   * @returns DN selecionado como number
   * @throws Error se a chave não existir, estiver vazia ou não for um número válido
   */
  static getSelectedVWDnAsNumber(radix: number = 10): number {
    const value = localStorage.getItem(this.KEY_SELECTED_VW_DN);
    
    if (value === null || value === undefined || value.trim() === '') {
      throw new Error(`LocalStorage key "${this.KEY_SELECTED_VW_DN}" não encontrada ou está vazia`);
    }
    
    const parsed = parseInt(value, radix);
    
    if (isNaN(parsed)) {
      throw new Error(`LocalStorage key "${this.KEY_SELECTED_VW_DN}" contém um valor inválido para conversão numérica: "${value}"`);
    }
    
    return parsed;
  }

  /**
   * Define o DN selecionado no localStorage
   * @param dn - DN selecionado (string ou number)
   */
  static setSelectedVWDn(dn: string | number): void {
    localStorage.setItem(this.KEY_SELECTED_VW_DN, String(dn));
  }

  /**
   * Verifica se o DN selecionado está salvo no localStorage
   * @returns true se a chave existe e não está vazia
   */
  static hasSelectedVWDn(): boolean {
    const value = localStorage.getItem(this.KEY_SELECTED_VW_DN);
    return value !== null && value !== undefined && value.trim() !== '';
  }

  /**
   * Remove o DN selecionado do localStorage
   */
  static removeSelectedVWDn(): void {
    localStorage.removeItem(this.KEY_SELECTED_VW_DN);
  }

  // ========== MÉTODOS PARA MARCADOR DE SSO ==========

  static setSsoAuthenticated(): void {
    localStorage.setItem(this.KEY_SSO_AUTHENTICATED, 'true');
  }

  static hasSsoAuthenticated(): boolean {
    return localStorage.getItem(this.KEY_SSO_AUTHENTICATED) === 'true';
  }

  static removeSsoAuthenticated(): void {
    localStorage.removeItem(this.KEY_SSO_AUTHENTICATED);
  }

  static hasAuthenticatedSession(): boolean {
    return this.hasSsoAuthenticated() && this.hasAccessToken() && this.hasVWUser();
  }

  // ========== MÉTODOS UTILITÁRIOS GLOBAIS ==========

  /**
   * Remove todas as chaves do localStorage relacionadas ao VW
   */
  static clearAllVWData(): void {
    this.removeVWUser();
    this.removeAccessToken();
    this.removeSelectedVWDn();
    this.removeSsoAuthenticated();
    this.removeLoginUsername();
  }
}
